import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

const DB_NAME = 'technicianOfflineDB';
const DB_VERSION = 2;
const STORE_PENDING = 'pendingActions';
const STORE_CACHED_JOBS = 'cachedJobs';
const STORE_CACHED_PHOTOS = 'cachedPhotos';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_PENDING)) {
        const store = db.createObjectStore(STORE_PENDING, { keyPath: 'id', autoIncrement: true });
        store.createIndex('ticketId', 'ticketId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_CACHED_JOBS)) {
        db.createObjectStore(STORE_CACHED_JOBS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_CACHED_PHOTOS)) {
        const photoStore = db.createObjectStore(STORE_CACHED_PHOTOS, { keyPath: 'localId', autoIncrement: true });
        photoStore.createIndex('ticketId', 'ticketId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function dbGet(storeName, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbGetAll(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbGetByIndex(storeName, indexName, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const index = tx.objectStore(storeName).index(indexName);
    const req = index.getAll(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbPut(storeName, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).put(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbAdd(storeName, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).add(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbDelete(storeName, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const syncInProgress = useRef(false);

  const refreshPendingCount = useCallback(async () => {
    try {
      const pending = await dbGetAll(STORE_PENDING);
      setPendingCount(pending?.length || 0);
    } catch (e) {
      setPendingCount(0);
    }
  }, []);

  const syncPendingActions = useCallback(async () => {
    if (syncInProgress?.current || !navigator.onLine) return;
    syncInProgress.current = true;
    setSyncing(true);
    try {
      const pending = await dbGetAll(STORE_PENDING);
      if (!pending?.length) return;

      for (const action of pending) {
        try {
          if (action?.type === 'UPDATE_STATUS') {
            await supabase?.from('tickets')?.update({ status_id: action?.payload?.statusId })?.eq('id', action?.payload?.ticketId);
          } else if (action?.type === 'SAVE_NOTE') {
            await supabase?.from('tickets')?.update({ notes: action?.payload?.notes })?.eq('id', action?.payload?.ticketId);
          } else if (action?.type === 'CHECK_IN') {
            await supabase?.from('tickets')?.update({
              status_id: action?.payload?.statusId,
              notes: action?.payload?.notes
            })?.eq('id', action?.payload?.ticketId);
          } else if (action?.type === 'UPLOAD_PHOTO') {
            // Re-upload photo from base64
            const base64 = action?.payload?.base64;
            const byteString = atob(base64?.split(',')?.[1]);
            const mimeString = base64?.split(',')?.[0]?.split(':')?.[1]?.split(';')?.[0];
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString?.length; i++) ia[i] = byteString?.charCodeAt(i);
            const blob = new Blob([ab], { type: mimeString });
            const fileName = action?.payload?.fileName;
            const { error: uploadError } = await supabase?.storage?.from('ticket-photos')?.upload(fileName, blob, { upsert: true });
            if (!uploadError) {
              const { data: urlData } = supabase?.storage?.from('ticket-photos')?.getPublicUrl(fileName);
              await supabase?.from('ticket_photos')?.insert({
                ticket_id: action?.payload?.ticketId,
                file_name: fileName,
                file_url: urlData?.publicUrl,
                caption: action?.payload?.caption || ''
              });
            }
          }
          await dbDelete(STORE_PENDING, action?.id);
        } catch (err) {
          console.error('Sync failed for action', action?.id, err);
        }
      }
      setLastSyncTime(new Date());
      await refreshPendingCount();
    } finally {
      syncInProgress.current = false;
      setSyncing(false);
    }
  }, [refreshPendingCount]);

  useEffect(() => {
    refreshPendingCount();

    const handleOnline = () => {
      setIsOnline(true);
      syncPendingActions();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingActions, refreshPendingCount]);

  const queueAction = useCallback(async (type, payload) => {
    await dbAdd(STORE_PENDING, { type, payload, queuedAt: new Date()?.toISOString() });
    await refreshPendingCount();
  }, [refreshPendingCount]);

  const cacheJobs = useCallback(async (jobs) => {
    for (const job of jobs) {
      await dbPut(STORE_CACHED_JOBS, job);
    }
  }, []);

  const getCachedJobs = useCallback(async () => {
    return await dbGetAll(STORE_CACHED_JOBS);
  }, []);

  const savePhotoLocally = useCallback(async (ticketId, base64, caption, fileName) => {
    const localId = await dbAdd(STORE_CACHED_PHOTOS, {
      ticketId,
      base64,
      caption,
      fileName,
      savedAt: new Date()?.toISOString(),
      synced: false
    });
    return localId;
  }, []);

  const getLocalPhotos = useCallback(async (ticketId) => {
    return await dbGetByIndex(STORE_CACHED_PHOTOS, 'ticketId', ticketId);
  }, []);

  const markPhotoSynced = useCallback(async (localId) => {
    const photo = await dbGet(STORE_CACHED_PHOTOS, localId);
    if (photo) await dbPut(STORE_CACHED_PHOTOS, { ...photo, synced: true });
  }, []);

  return {
    isOnline,
    pendingCount,
    syncing,
    lastSyncTime,
    queueAction,
    syncPendingActions,
    cacheJobs,
    getCachedJobs,
    savePhotoLocally,
    getLocalPhotos,
    markPhotoSynced
  };
}
